import { Link } from "react-router-dom";
import { PageHeader, CTASection, Disclaimer } from "@/components/Shared";
import { Sparkles, Award, Globe, Trophy, FlaskConical, CheckCircle2, ArrowRight, ExternalLink } from "lucide-react";

const TARGET = [
  { icon: FlaskConical, title: "Researchers & Thought Leaders", desc: "Global experts in academia with substantial publication records and high citation counts." },
  { icon: Sparkles, title: "Entrepreneurs", desc: "Both established and emerging innovators, especially those with experience in successful State and Territory-led programs." },
  { icon: Award, title: "Innovative Investors", desc: "Individuals committed to impactful investments, with prioritisation of quality and influence over financial benchmarks." },
  { icon: Trophy, title: "Athletes & Creatives", desc: "Those who represent Australia on the global stage in sport, the arts or other creative domains." },
];

const PRIORITIES = [
  { label: "Priority 1", desc: "Exceptional candidates from any sector who are global experts and recipients of international 'top of field' level awards." },
  { label: "Priority 2", desc: "Candidates from any sector nominated on an approved Form 1000 by an expert Australian Commonwealth, State or Territory Government agency." },
  { label: "Priority 3", desc: "Candidates with exceptional and outstanding achievements in a Tier One sector — Critical Technologies · Health Industries · Renewables and Low Emission Technologies." },
  { label: "Priority 4", desc: "Candidates with exceptional and outstanding achievements in a Tier Two sector — Agri-food & AgTech · Defence Capabilities and Space · Education · Financial Services & FinTech · Infrastructure & Transport · Resources." },
];

const KEY = [
  "Replaces the Global Talent visa, but retains the Subclass 858 visa classification.",
  "Provides an Australian migration option for a broad range of high-calibre talent.",
  "Expressions of Interest (EOIs) remain valid for 2 years, offering candidates more time to meet eligibility requirements.",
  "Applications are by invitation only — EOIs more closely reflect other invitation-only visas.",
  "Direct pathway to Australian permanent residence.",
];

const ELIGIBILITY = [
  "Internationally recognised record of exceptional and outstanding achievement in a profession, sport, the arts, or academia and research.",
  "Still a prominent participant in your field.",
  "Would be an asset to the Australian community.",
  "No difficulty obtaining employment or becoming established independently in Australia.",
  "Nomination on a completed Form 1000 by an eligible Australian citizen, permanent resident, eligible New Zealand citizen, or Australian organisation with a national reputation.",
  "Functional English (or pay the second VAC).",
  "If under 18 or 55+, must also be of 'exceptional benefit' to the Australian community.",
];

export default function NationalInnovationVisa() {
  return (
    <div>
      <PageHeader
        eyebrow="Subclass 858 · Direct PR · Invitation Only"
        title="National Innovation Visa."
        subtitle="Australia's premier permanent residence pathway for individuals with an internationally recognised record of exceptional and outstanding achievement — replacing the Global Talent visa from 6 December 2024."
        image="/assets/hero-australia.jpg"
      />

      {/* Intro */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-slate-600 leading-relaxed text-[15px] space-y-5">
          <p>
            The Australian Government has implemented the new <strong className="text-[#29324A]">National Innovation Visa (NIV)</strong>, replacing the Global Talent visa (Subclass 858) from 6 December 2024. The NIV is designed to attract exceptionally talented migrants who will drive growth in sectors of national importance — including researchers, entrepreneurs, innovative investors, athletes and creatives.
          </p>
          <p>
            Applications are <strong>by invitation only</strong>. After submitting an Expression of Interest (EOI), invitations are issued in a strict priority order. Priority 1 and 2 candidates are invited as soon as they are identified; Priority 3 and 4 candidates are invited monthly.
          </p>
          <div className="bg-[#F8F4FB] border-l-4 border-[#C43A89] p-5 text-sm">
            <strong className="text-[#29324A]">Program status (current):</strong> Since the program commenced, more than 9,000 Expressions of Interest have been submitted, with approximately 304 invitations issued and 85 visas granted — meaning the chances of invitation are generally low and highly selective.
          </div>
        </div>
      </section>

      {/* Key facts */}
      <section className="py-14 md:py-16 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-4 flex items-center gap-3">
            <span className="brand-dot bg-[#C43A89]" /> Key Facts
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] leading-tight">
            What makes the National Innovation Visa different.
          </h2>

          <ul className="mt-8 grid md:grid-cols-2 gap-x-10 gap-y-3 text-sm text-slate-600">
            {KEY.map((k, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 className="h-4 w-4 text-[#29324A] mt-0.5 shrink-0" />
                <span>{k}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Target audience */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] leading-tight mb-10">
            Who the NIV is for
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200">
            {TARGET.map((t, i) => (
              <div key={i} className="bg-white p-6">
                <t.icon className="h-7 w-7 text-[#C43A89] mb-4" />
                <h3 className="font-display text-lg text-[#29324A] mb-2">{t.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="py-14 md:py-20 bg-[#F8F4FB]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center gap-3">
              <span className="brand-dot bg-[#C43A89]" /> Eligibility
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] leading-tight">
              What you need to qualify
            </h2>
            <p className="mt-4 text-sm text-slate-600 leading-relaxed">
              The National Innovation Visa requires applicants to demonstrate an internationally recognised record of exceptional and outstanding achievement, plus support from a qualifying nominator using Form 1000.
            </p>
          </div>
          <ul className="space-y-3 text-sm text-slate-600">
            {ELIGIBILITY.map((e, i) => (
              <li key={i} className="flex items-start gap-3 bg-white p-4 border-l-2 border-[#29324A]">
                <CheckCircle2 className="h-4 w-4 text-[#C43A89] mt-0.5 shrink-0" />
                <span>{e}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Priorities */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center gap-3">
            <span className="brand-dot bg-[#C43A89]" /> Invitation Priority Matrix
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] leading-tight mb-10">
            How invitations are issued
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {PRIORITIES.map((p, i) => (
              <div key={i} className="bg-white border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[10px] font-bold tracking-[0.22em] text-white bg-[#29324A] px-3 py-1 uppercase">
                    {p.label}
                  </span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NIV vs SID */}
      <section className="py-14 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] leading-tight mb-8">
            NIV vs Skills in Demand (Specialist Skills stream)
          </h2>
          <div className="overflow-x-auto border border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#29324A] text-white">
                  <th className="text-left px-4 md:px-6 py-3 font-display">Comparison</th>
                  <th className="text-left px-4 md:px-6 py-3 font-display">Skills in Demand (Subclass 482) — Specialist Skills</th>
                  <th className="text-left px-4 md:px-6 py-3 font-display bg-[#C43A89]">National Innovation Visa (Subclass 858)</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {[
                  ["Applicant profile", "Highly skilled, earning at least AUD 135,000, with an employer sponsor", "Internationally recognised record of exceptional talent, prominent in their field"],
                  ["Visa length", "Up to 4 years (pathway to PR)", "Direct to permanent residence"],
                  ["Sponsorship", "Must have an approved sponsor", "Must be nominated via approved Form 1000"],
                  ["English", "Competent English", "Functional English or pay second VAC"],
                  ["Age", "Any age (PR rules separate)", "Any age (under 18 / 55+ must show 'exceptional benefit')"],
                  ["Occupation restrictions", "Most occupations except trades, machinery operators, drivers and labourers", "No occupation restrictions"],
                  ["Grants per year", "Demand driven", "Planned program — invitation required"],
                ].map((row, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-4 md:px-6 py-3 font-semibold text-[#29324A]">{row[0]}</td>
                    <td className="px-4 md:px-6 py-3 text-slate-600">{row[1]}</td>
                    <td className="px-4 md:px-6 py-3 text-slate-700 bg-[#FCFBFD]">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* StudyNet support + CTA */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Globe className="h-10 w-10 text-[#C43A89] mx-auto mb-5" />
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] leading-tight">
            Considering the NIV? Speak with a Registered Migration Agent first.
          </h2>
          <p className="mt-4 text-sm text-slate-600 leading-relaxed">
            With invitation rates currently low and the assessment of "internationally recognised exceptional and outstanding achievement" highly evidence-driven, the National Innovation Visa requires careful preparation. Our team can review your profile and identify the strongest case before you submit an EOI.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/book-consultation"
              data-testid="niv-book-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white font-semibold py-3.5 px-7 transition-colors btn-shimmer text-xs uppercase tracking-wider"
            >
              Book a Confidential NIV Consultation <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/national-innovation-858"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#29324A] text-[#29324A] hover:bg-[#29324A] hover:text-white font-semibold py-3.5 px-7 transition-colors text-xs uppercase tracking-wider"
            >
              Department of Home Affairs <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Disclaimer>
            Information on this page is general in nature and may change as Department of Home Affairs policy evolves. The National Innovation Visa is an evidence-intensive, invitation-only program — please discuss your specific circumstances with a Registered Migration Agent before lodging an EOI.
          </Disclaimer>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
